/**
 * @fileoverview Main navigation bar component for the Onicorn application
 * @module components/layout/NavigationBar
 */

import { useState, useEffect, lazy, Suspense, useMemo, useCallback, type ReactElement, type MouseEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { flushSync } from 'react-dom';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuLink,
} from '@/components/ui/navigation/navigation-menu';
import { Button } from '@/components/ui/form/button';
import { useEnhancedAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { Menu, X, HelpCircle } from 'lucide-react';

// Lazy load heavy components to improve initial load performance
const SimpleCustomConnectButton = lazy(() =>
  import('@/features/auth/components/CustomConnectButton').then(m => ({
    default: m.SimpleCustomConnectButton,
  }))
);
const ContactFormModal = lazy(() =>
  import('@/features/contact').then(m => ({
    default: m.ContactFormModal,
  }))
);

/**
 * Configuration for navigation links in the header.
 */
interface NavigationLink {
  /** Display text for the navigation link */
  label: string;
  /** URL path for the navigation link */
  href: string;
  /** Whether this navigation item requires authentication */
  requiresAuth?: boolean;
}

/**
 * Props for the NavigationBar component.
 */
interface NavigationBarProps {
  /** Additional CSS classes to apply to the navigation bar */
  className?: string;
}

/**
 * Main navigation bar component for the Onicorn Web3 application.
 *
 * Provides a responsive navigation header with three main sections:
 * - Logo/brand area (left)
 * - Main navigation links (center): Deals, KOLs, Dashboard
 * - Enhanced wallet connection button with social login (right)
 *
 * Features:
 * - TanStack Router integration for type-safe navigation
 * - Conditional Dashboard link based on authentication status
 * - Active route highlighting
 * - Responsive design that adapts to different screen sizes
 * - Enhanced authentication with CustomConnectButton (wallet + social login)
 * - Accessibility support with ARIA labels and keyboard navigation
 * - shadcn/ui components for consistent styling
 *
 * @component
 * @example
 * ```tsx
 * <NavigationBar className="sticky top-0 z-50" />
 * ```
 */
export function NavigationBar({ className }: NavigationBarProps): ReactElement {
  const { isAuthenticated, user, signOut, isSigningOut } = useEnhancedAuth();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Detect mobile to avoid rendering heavy Dynamic.xyz components on mobile
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768; // md breakpoint
  }, []);

  /**
   * Lock body scroll when mobile menu is open to prevent background scrolling.
   *
   * IMPORTANT: We use overflow:hidden instead of position:fixed because
   * position:fixed causes a ~1800ms delay on mobile due to:
   * - Full document re-layout
   * - Disruption of mobile scroll optimizations
   * - Compositor layer recalculation
   *
   * The trade-off is that iOS Safari may still allow some bounce scrolling,
   * but this is acceptable compared to a 2-second menu delay.
   */
  useEffect(() => {
    if (isMobile && isMobileMenuOpen) {
      // Mobile-friendly scroll lock: just overflow hidden, no position fixed
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else if (isMobile) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    return () => {
      if (isMobile) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    };
  }, [isMobileMenuOpen, isMobile]);

  /**
   * Preload dashboard chunks when mobile menu opens (for authenticated users).
   *
   * PERFORMANCE: By preloading when menu opens, the dashboard chunks are already
   * cached by the time user clicks Dashboard. This eliminates the loading delay.
   *
   * IMPORTANT: Must match the exact import paths used in lazy() calls:
   * - Route uses: import('@/features/dashboard/components/DashboardPage')
   * - UserTypeDashboard uses: import('../components/KOLDashboard')
   */
  useEffect(() => {
    if (isMobile && isMobileMenuOpen && isAuthenticated) {

      // Preload in sequence: DashboardPage first, then KOLDashboard
      // These MUST match the exact paths used in lazy() imports
      import('@/features/dashboard/components/DashboardPage').then(() => {

        // Now preload KOLDashboard (most common user type)
        return import('@/features/dashboard/components/KOLDashboard');
      }).then(() => {

      });
    }
  }, [isMobile, isMobileMenuOpen, isAuthenticated]);

  /**
   * Navigation links configuration.
   * Dashboard link is only shown when user is authenticated.
   */
  const navigationLinks: NavigationLink[] = [
    { label: 'Deals', href: '/deals' },
    { label: 'KOLs', href: '/kols' },
    { label: 'Dashboard', href: '/dashboard', requiresAuth: true },
  ];

  const navigate = useNavigate();

  /**
   * Handles mobile menu link clicks with proper paint synchronization.
   *
   * MOBILE FREEZE FIX: On mobile, navigation blocks the main thread before
   * the menu close can paint. We fix this by:
   * 1. Preventing Link's default navigation
   * 2. Using flushSync to force React to commit the menu close
   * 3. Waiting for actual paint via requestAnimationFrame
   * 4. Then navigating programmatically
   *
   * This ensures the user sees the menu close BEFORE navigation starts.
   */
  const handleMobileLinkClick = useCallback((e: MouseEvent<HTMLAnchorElement>, href: string): void => {
    if (isMobile && isMobileMenuOpen) {

      // Prevent Link's default navigation
      e.preventDefault();

      // Force React to synchronously commit the menu close
      flushSync(() => {
        setIsMobileMenuOpen(false);
      });

      // Wait for browser to actually paint the closed menu
      // Double RAF ensures we're past the paint
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {

          navigate({ to: href });
        });
      });
    }
    // On desktop, let Link handle navigation normally (no preventDefault)
  }, [isMobile, isMobileMenuOpen, navigate]);

  /**
   * Handle sign out from mobile menu.
   */
  const handleMobileSignOut = async (): Promise<void> => {
    try {
      setIsMobileMenuOpen(false);
      await signOut();
    } catch {
      // Error handled by auth context
    }
  };

  return (
    <header className={cn('fixed top-0 inset-x-0 z-50 px-2 py-2 md:top-2.5 md:left-0 md:right-0 md:px-0 md:py-0', className)}>
      <div className="md:flex md:justify-center md:w-full md:px-4">
        <nav
          className={cn(
            "flex h-14 md:h-[70px] items-center justify-between",
            "w-full max-w-full md:w-[1300px] md:max-w-full",
            "border border-onicorn-gray-500 rounded-lg md:rounded-[9px]",
            "px-2 md:pl-[40px] md:pr-[80px]",
            "bg-black/0 backdrop-blur-xs",
            "shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]",
            "drop-shadow-[0_4px_4px_rgba(0,0,0,1)]",
            "border-white/20"
          )}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Logo Section */}
          <Link
            to="/"
            className="flex-shrink-0 hover:opacity-80 transition-opacity md:transition-all md:duration-300 flex items-center gap-2"
          >
            <img
              src="/icons/logo.svg"
              alt="Onicorn"
              className="h-4 w-auto md:w-[122px] md:h-[24px]"
            />
            <span className="px-1.5 py-0.5 text-[8px] md:text-[9px] text-onicorn-gray-400 font-medium tracking-wider bg-onicorn-gray-400/10 border border-onicorn-gray-400/30 rounded">
              BETA
            </span>
          </Link>

          {/* Navigation Links - Desktop only */}
          <div className="hidden md:flex flex-1 justify-center items-center">
            <div className="flex justify-evenly w-full">
              {navigationLinks
                .filter(link => !link.requiresAuth || isAuthenticated)
                .map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    className={cn(
                      'font-medium transition-all duration-200',
                      'text-onicorn-gray-400 hover:text-onicorn-white',
                      'data-[status=active]:text-onicorn-white',
                      'bg-transparent hover:bg-transparent focus:bg-transparent',
                      'hover:scale-110 transform origin-center'
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Help/Contact Button - Desktop only, shown when authenticated */}
            {isAuthenticated && user && (
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="hidden md:flex items-center justify-center p-2 text-onicorn-gray-400 hover:text-onicorn-white transition-colors rounded-lg hover:bg-white/5"
                aria-label="Contact support"
                title="Contact Us"
              >
                <HelpCircle size={20} />
              </button>
            )}

            {/* Connect Button - Show Dynamic widget on both mobile and desktop */}
            <div className="flex-shrink-0">
              <Suspense fallback={<div className="w-20 h-8 bg-onicorn-gray-800 rounded animate-pulse" />}>
                <SimpleCustomConnectButton hideSignOut={isMobile} />
              </Suspense>
            </div>

            {/* Burger Menu Icon - Mobile only */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-1.5 text-white hover:text-onicorn-gray-400 transition-colors"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu Drawer */}
      <div
        className={cn(
          "md:hidden fixed inset-x-0 top-[58px] bottom-0 bg-black overflow-hidden",
          isMobileMenuOpen ? "block" : "hidden"
        )}
      >
        <div
          className="flex flex-col h-full justify-between pt-6 px-4 pb-6 max-w-full overflow-y-auto"
        >
          {/* Navigation Links */}
          <div className="w-full max-w-full">
            <NavigationMenu className="w-full max-w-full">
              <NavigationMenuList className="flex-col space-y-2 w-full max-w-full">
                {navigationLinks
                  .filter(link => !link.requiresAuth || isAuthenticated)
                  .map((link) => (
                    <NavigationMenuItem key={link.label} className="w-full max-w-full">
                      <NavigationMenuLink asChild>
                        <Link
                          to={link.href}
                          onClick={(e) => handleMobileLinkClick(e, link.href)}
                          className={cn(
                            'w-full max-w-full justify-center text-center font-medium px-4 py-3',
                            'text-onicorn-gray-400 hover:text-onicorn-white text-base',
                            'data-[status=active]:text-onicorn-white',
                            'bg-transparent hover:bg-white/5 focus:bg-white/5',
                            'rounded-lg border border-transparent hover:border-white/10',
                            'block overflow-hidden'
                          )}
                        >
                          {link.label}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Action Buttons Section - Only shown when authenticated */}
          {isAuthenticated && user && (
            <div className="flex flex-col gap-3 mt-auto w-full max-w-full pt-6 border-t border-onicorn-gray-700">
              {/* Contact Us Button */}
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsContactModalOpen(true);
                }}
                variant="outline"
                className={cn(
                  "w-full border-onicorn-gray-700 bg-transparent",
                  "text-onicorn-white hover:bg-onicorn-gray-800",
                  "hover:border-onicorn-green/50 hover:text-onicorn-green"
                )}
              >
                <HelpCircle size={16} className="mr-2" />
                Contact Us
              </Button>

              {/* Sign Out Button */}
              <Button
                onClick={handleMobileSignOut}
                disabled={isSigningOut}
                variant="outline"
                className={cn(
                  "w-full border-onicorn-gray-700 bg-transparent",
                  "text-onicorn-white hover:bg-onicorn-gray-800",
                  "hover:border-red-500/50 hover:text-red-400",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isSigningOut ? 'Signing out...' : 'Sign Out'}
              </Button>
            </div>
          )}

        </div>
      </div>

      {/* Contact Form Modal */}
      <Suspense fallback={null}>
        <ContactFormModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          defaultEmail={user?.email}
        />
      </Suspense>
    </header>
  );
}
